import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useCurrentUser } from "../context/AuthContext";
import SaunaForm, { SaunaFields, emptySaunaFields } from "../components/SaunaForm";
import SaunaImageManager from "../components/SaunaImageManager";
import AvailabilityEditor from "../components/AvailabilityEditor";

const GET_SAUNA = gql`
  query SaunaForEdit($id: Int!) {
    saunaById(id: $id) {
      id ownerId name description addressLine city state postalCode country
      latitude longitude capacity pricePerHourCents isActive saunaType requiresApproval
      saunaAmenitiesBySaunaId {
        nodes { amenityId }
      }
    }
  }
`;

const UPDATE_SAUNA = gql`
  mutation UpdateMySauna(
    $id: Int!
    $name: String!
    $description: String
    $addressLine: String
    $city: String
    $state: String
    $postalCode: String
    $country: String
    $latitude: BigFloat
    $longitude: BigFloat
    $capacity: Int!
    $pricePerHourCents: Int!
    $saunaType: SaunaType
    $amenityIds: [Int!]
    $requiresApproval: Boolean
  ) {
    updateMySauna(input: {
      id: $id
      name: $name
      description: $description
      addressLine: $addressLine
      city: $city
      state: $state
      postalCode: $postalCode
      country: $country
      latitude: $latitude
      longitude: $longitude
      capacity: $capacity
      pricePerHourCents: $pricePerHourCents
      saunaType: $saunaType
      amenityIds: $amenityIds
      requiresApproval: $requiresApproval
    }) {
      sauna { id requiresApproval }
    }
  }
`;

const DELETE_SAUNA = gql`
  mutation DeleteMySauna($id: Int!) {
    deleteMySauna(input: { id: $id }) {
      sauna { id }
    }
  }
`;

export default function SaunaEdit() {
  const { id } = useParams<{ id: string }>();
  const saunaId = parseInt(id!, 10);
  const navigate = useNavigate();
  const userId = useCurrentUser();

  const { data, loading: fetching } = useQuery(GET_SAUNA, { variables: { id: saunaId } });
  const [updateSauna, { loading: updating }] = useMutation(UPDATE_SAUNA, { refetchQueries: ["MySaunas", "SaunaForEdit"] });
  const [deleteSauna, { loading: deleting }] = useMutation(DELETE_SAUNA, { refetchQueries: ["MySaunas"] });

  const [fields, setFields] = useState<SaunaFields>(emptySaunaFields);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const s = data?.saunaById;
    if (!s) return;
    if (s.ownerId !== userId) { navigate("/dashboard", { replace: true }); return; }
    setFields({
      name: s.name ?? "",
      description: s.description ?? "",
      addressLine: s.addressLine ?? "",
      city: s.city ?? "",
      state: s.state ?? "",
      postalCode: s.postalCode ?? "",
      country: s.country ?? "",
      capacity: String(s.capacity),
      pricePerHourCents: String(s.pricePerHourCents),
      lat: s.latitude ? Number(s.latitude) : null,
      lng: s.longitude ? Number(s.longitude) : null,
      saunaType: s.saunaType ?? null,
      amenityIds: s.saunaAmenitiesBySaunaId?.nodes.map((n: any) => n.amenityId) ?? [],
      requiresApproval: s.requiresApproval ?? false,
    });
  }, [data, userId, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      const { errors } = await updateSauna({
        variables: {
          id: saunaId,
          name: fields.name,
          description: fields.description || null,
          addressLine: fields.addressLine || null,
          city: fields.city || null,
          state: fields.state || null,
          postalCode: fields.postalCode || null,
          country: fields.country || null,
          latitude: fields.lat,
          longitude: fields.lng,
          capacity: parseInt(fields.capacity, 10),
          pricePerHourCents: parseInt(fields.pricePerHourCents, 10),
          saunaType: fields.saunaType || null,
          amenityIds: fields.amenityIds,
          requiresApproval: fields.requiresApproval,
        },
      });
      if (errors) { setError(errors[0]?.message ?? "Failed to save"); return; }
      setSaved(true);
    } catch (err: any) {
      setError(err?.message ?? "Something went wrong");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this sauna? This cannot be undone.")) return;
    try {
      await deleteSauna({ variables: { id: saunaId } });
      navigate("/dashboard");
    } catch (err: any) {
      setError(err?.message ?? "Failed to delete");
    }
  };

  if (fetching) return <p style={{ padding: 24 }}>Loading...</p>;
  if (!data?.saunaById) return <p style={{ padding: 24 }}>Sauna not found.</p>;

  return (
    <div style={{ maxWidth: 640, margin: "40px auto", padding: "0 24px 60px" }}>
      <h2 style={{ marginBottom: 28 }}>Edit listing</h2>

      {saved && <p style={{ color: "#16a34a", marginBottom: 16 }}>Saved.</p>}

      <SaunaForm
        fields={fields}
        onChange={setFields}
        onSubmit={handleSubmit}
        loading={updating}
        error={error}
        submitLabel="Save changes"
        extraActions={
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            style={deleteBtnStyle}
          >
            {deleting ? "Deleting..." : "Delete listing"}
          </button>
        }
      />

      <hr style={{ margin: "40px 0", borderColor: "#e5e7eb" }} />

      <SaunaImageManager saunaId={saunaId} />

      <hr style={{ margin: "40px 0", borderColor: "#e5e7eb" }} />

      <h3 style={{ margin: "0 0 16px", fontSize: 16 }}>Availability</h3>
      <AvailabilityEditor saunaId={saunaId} />
    </div>
  );
}

const deleteBtnStyle: React.CSSProperties = {
  padding: "10px 20px", background: "#ef4444", color: "#fff",
  border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
